'use client';

import { useState } from 'react';
import * as RadixPopover from '@radix-ui/react-popover';
import { FeatherCalendar } from '@subframe/core';

import { brParaIso, isoParaBr, maskDateBr } from '@/shared/utils/periodoFiltro';

import { CalendarioBR } from './CalendarioBR';
import { TextField } from './TextField';

interface CampoDataBRProps {
  label: string;
  /** Texto exibido em `dd/mm/aaaa` — pode estar incompleto enquanto o usuário digita. */
  texto: string;
  /** Recebe o texto já mascarado, seja da digitação ou do dia escolhido no calendário. */
  onTextoChange: (texto: string) => void;
  onBlur?: () => void;
}

/**
 * Campo de data com máscara `dd/mm/aaaa` e calendário em popover. `input type="date"` herdaria o
 * formato do navegador (mm/dd/aaaa em inglês); o texto mascarado mais o CalendarioBR mantém o
 * formato brasileiro em qualquer locale.
 */
export function CampoDataBR({ label, texto, onTextoChange, onBlur }: CampoDataBRProps) {
  const [calendarioAberto, setCalendarioAberto] = useState(false);

  const escolherDia = (iso: string) => {
    onTextoChange(isoParaBr(iso));
    setCalendarioAberto(false);
  };

  return (
    <TextField
      className="flex-1"
      label={label}
      variant="outline"
      iconRight={
        <RadixPopover.Root open={calendarioAberto} onOpenChange={setCalendarioAberto}>
          <RadixPopover.Trigger asChild>
            <button type="button" aria-label={`Abrir calendário: ${label}`}>
              <FeatherCalendar />
            </button>
          </RadixPopover.Trigger>
          <RadixPopover.Portal>
            <RadixPopover.Content
              align="end"
              sideOffset={8}
              className="z-[10010] rounded-[16px] border border-solid border-neutral-200 bg-white p-3 shadow-lg"
            >
              <CalendarioBR value={brParaIso(texto) ?? ''} onChange={escolherDia} />
            </RadixPopover.Content>
          </RadixPopover.Portal>
        </RadixPopover.Root>
      }
    >
      <TextField.Input
        placeholder="dd/mm/aaaa"
        inputMode="numeric"
        value={texto}
        onChange={(event) => onTextoChange(maskDateBr(event.target.value))}
        onBlur={onBlur}
      />
    </TextField>
  );
}
